import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { fetchCurrentUser } from '../../../ducks/user.duck';

import routeConfiguration, { ACCOUNT_SETTINGS_PAGES } from '../../../routeConfiguration';
import { petrol, yellow } from '../../../styles/mui/colors';
import {
  computeStripeReturnUrl,
  openStripeHosted,
} from '../../../util/mobile/externalCheckout';
import { createResourceLocatorString, matchPathname } from '../../../util/routes';
import IconBell from '../../Icons/IconBell';
import IconCard from '../../Icons/IconCard';
import IconCreditcard from '../../Icons/IconCreditcard';
import IconDelete from '../../Icons/IconDelete';
import IconLock from '../../Icons/IconLock';
import IconMessage from '../../Icons/IconMessage';
import IconMoney from '../../Icons/IconMoney';

const SettingsMenuComponent = ({ onClose, setSettingsMenuOpen }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const currentPage = matchPathname(location.pathname, routeConfiguration())[0].route.name;

  const currentPageColor = (page) => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? yellow.main : petrol.main;
  };

  // Function to navigate to a specific page
  const navigateToPage = (pageName) => {
    onClose();
    const url = createResourceLocatorString(pageName, routeConfiguration(), {}, {}, intl.locale);
    navigate(url);
  };

  // New: List of account setting pages
  const accountSettingsPages = [
    {
      name: 'ContactSettingsPage',
      label: intl.formatMessage({ id: 'TopbarMobileMenu.contactDetailsLink' }),
      icon: <IconMessage color={currentPageColor('ContactSettingsPage')} />,
    },
    {
      name: 'PasswordChangePage',
      label: intl.formatMessage({ id: 'TopbarMobileMenu.changePasswordLink' }),
      icon: <IconLock color={currentPageColor('PasswordChangePage')} />,
    },
    {
      name: 'PassSettingsPage',
      label: intl.formatMessage({ id: 'TopbarMobileMenu.annualPassLink' }),
      icon: <IconCard color={currentPageColor('PassSettingsPage')} />,
    },
    {
      name: 'PaymentMethodsPage',
      label: intl.formatMessage({ id: 'TopbarMobileMenu.paymentMethodsLink' }),
      icon: <IconCreditcard color={currentPageColor('PaymentMethodsPage')} />,
      onClick: async () => {
        // Loaded on demand: a static import would pull the code-split PassSettingsPage duck into
        // the main bundle. Awaiting it also registers that duck's reducer, which the thunk reads.
        const { createPortalSession } = await import(
          '../../../containers/PassSettingsPage/PassSettingsPage.duck'
        );
        const url = await dispatch(createPortalSession());
        if (!url) return;
        openStripeHosted(url, {
          returnUrlPrefix: computeStripeReturnUrl(),
          onReturn: () => {
            dispatch(fetchCurrentUser());
          },
        });
      },
    },
    {
      name: 'StripePayoutPage',
      label: intl.formatMessage({ id: 'TopbarMobileMenu.stripePayoutLink' }),
      icon: <IconMoney color={currentPageColor('StripePayoutPage')} />,
    },
    {
      name: 'NotificationSettingsPage',
      label: intl.formatMessage({ id: 'TopbarMobileMenu.notificationSettingsLink' }),
      icon: <IconBell color={currentPageColor('NotificationSettingsPage')} />,
    },
    {
      name: 'DeleteUserPage',
      icon: <IconDelete color={currentPageColor('DeleteUserPage')} />,
      label: intl.formatMessage({ id: 'LayoutWrapperAccountSettingsSideNav.deleteUserTabTitle' }),
    },
    // Add more pages as needed
  ];

  return (
    <Stack alignItems="flex-start" spacing={2} width="100%">
      <Box
        alignItems="center"
        display="flex"
        justifyContent="center"
        pb={4}
        position="relative"
        width="100%"
      >
        <IconButton
          onClick={() => setSettingsMenuOpen(false)}
          sx={{
            p: 0,
            position: 'absolute',
            left: 0,
          }}
        >
          <ArrowBackIcon sx={{ color: petrol.main }} />
        </IconButton>

        <Typography sx={{ fontWeight: 'bold' }} variant="h3">
          <FormattedMessage id="MobileMenu.accountSettings" />
        </Typography>
      </Box>

      {accountSettingsPages.map((page) => (
        <Button
          key={page.name}
          onClick={() => page.onClick ? page.onClick() : navigateToPage(page.name)}
          startIcon={page.icon}
          sx={{ p: 0, justifyContent: 'flex-start' }}
          variant="text"
        >
          <Typography sx={{ color: currentPageColor(page.name) }} variant="body1">
            {page.label}
          </Typography>
        </Button>
      ))}
      <Divider />
    </Stack>
  );
};

SettingsMenuComponent.displayName = 'SettingsMenu';
const SettingsMenu = memo(SettingsMenuComponent);

export default SettingsMenu;
